import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../api/auth';
import styles from './LoginPage.module.css';
import { useTranslation } from 'react-i18next';

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const email = useMemo(() => params.get('email') || '', [params]);
  const token = useMemo(() => params.get('token') || '', [params]);

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('auth');

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!token || !email) {
      setError(t('resetPassword.invalidLink'));
      return;
    }

    if (password.length < 8) {
      setError(t('resetPassword.tooShort'));
      return;
    }

    if (password !== passwordConfirmation) {
      setError(t('resetPassword.mismatch'));
      return;
    }

    setLoading(true);
    try {
      const res = await resetPassword({
        token,
        email,
        password,
        password_confirmation: passwordConfirmation,
      });

      setSuccess(res?.message || t('resetPassword.success'));
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) {
        const messages = Object.values(data.errors).flat();
        setError(messages.join(' / '));
      } else {
        setError(data?.message || t('resetPassword.failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('resetPassword.title')}</h2>

      {error && <p className={styles.error}>{error}</p>}
      {success && <p>{success}</p>}

      <label htmlFor="reset-email" style={{ display: 'block', marginBottom: 4 }}>{t('resetPassword.emailLabel')}</label>
      <input
        id="reset-email"
        type="email"
        value={email}
        readOnly
        style={{ display: 'block', marginBottom: '10px' }}
      />

      <label htmlFor="reset-password" style={{ display: 'block', marginBottom: 4 }}>{t('resetPassword.newPasswordLabel')}</label>
      <input
        id="reset-password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={t('resetPassword.newPasswordPlaceholder')}
        autoComplete="new-password"
        style={{ display: 'block', marginBottom: '10px' }}
      />

      <label htmlFor="reset-password-confirmation" style={{ display: 'block', marginBottom: 4 }}>{t('resetPassword.confirmPasswordLabel')}</label>
      <input
        id="reset-password-confirmation"
        type="password"
        value={passwordConfirmation}
        onChange={(e) => setPasswordConfirmation(e.target.value)}
        placeholder={t('resetPassword.confirmPasswordPlaceholder')}
        autoComplete="new-password"
        style={{ display: 'block', marginBottom: '10px' }}
      />

      <button className={styles.loginButton} onClick={handleSubmit} disabled={loading}>
        {loading ? t('resetPassword.updating') : t('resetPassword.submit')}
      </button>

      <div className={styles.linkRow}>
        <Link to="/login" style={{ color: 'blue', textDecoration: 'underline' }}>
          {t('resetPassword.backToLogin')}
        </Link>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
